import { Component, ElementRef, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { ScreenReaderAnnouncerService } from '@app/services/screen-reader-announcer.service';
import { CommonModule } from '@angular/common';
import {
  SummaryHeaderComponent
} from '@app/shared/summary/summary-header/summary-header.component';
import { CareerGoalComponent } from '@app/shared/summary/career-goal/career-goal.component';
import { MatchScoreComponent } from '@app/shared/summary/match-score/match-score.component';
import { SalaryGrowthComponent } from '@app/shared/summary/salary-growth/salary-growth.component';
// import { EducationComponent } from '@app/shared/summary/education/education.component';
// import { EmployersComponent } from '@app/shared/summary/employers/employers.component';
// import { JobDetailsComponent } from '@app/shared/summary/job-details/job-details.component';
// import { JobScheduleComponent } from '@app/shared/summary/job-schedule/job-schedule.component';
import {
  RelatedCareersComponent
} from '@app/shared/summary/related-careers/related-careers.component';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { CareerResultSchema } from '@app/schemas/career-matches';
import { CareerDetailsService } from '@app/services/api/career-details/career-details.service';
import { CareerDetailResponse } from '@app/schemas/career-details';
import { catchError, of, Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ScoresComponent } from '@app/shared/survey/scores/scores.component';
import { SurveysService } from '@app/services/api/surveys/surveys.service';
import { environment } from 'environments/environment';
import { getCareerIcon } from '@app/utils/careers';
import { ONET_LOCAL_KEY } from '@app/utils/constants';
import { analytics } from '@app/events/segment/analytics';

@Component({
  selector: 'app-career-details',
  imports: [
    CommonModule,
    SummaryHeaderComponent,
    CareerGoalComponent,
    MatchScoreComponent,
    SalaryGrowthComponent,
    // EducationComponent,
    // EmployersComponent,
    // JobDetailsComponent,
    // JobScheduleComponent,
    // RelatedCareersComponent,
  ],
  templateUrl: './career-details.component.html',
  styleUrl: './career-details.component.css'
})
export class CareerDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('contentDiv') contentDiv!: ElementRef<HTMLDivElement>;
  private scrollTimeout: any;
  public onetCode!: string;
  public careerData!: CareerResultSchema | undefined;
  public allCareerMatches: CareerResultSchema[] = [];
  public careerDetails!: CareerDetailResponse | undefined;
  public loading = true;
  public loadingMessage = '';
  public error: string | null = null;
  readonly dialog = inject(MatDialog);
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<{ careerMatches: CareerResultSchema[], riasecScore: any }>,
    private careerDetailsService: CareerDetailsService,
    private surveysService: SurveysService,
    private announcer: ScreenReaderAnnouncerService
  ) { }

  ngOnInit() {
    // Subscribe to store for supplementary data (match scores, icons)
    this.store.select('careerMatches').pipe(takeUntil(this.destroy$)).subscribe(careerMatches => {
      this.allCareerMatches = careerMatches;
      if (careerMatches.length > 0 && this.onetCode) {
        this.careerData = careerMatches.find(e => e.onet_code == this.onetCode);
      }
    });

    // React to route param changes and load career details from API
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const newOnetCode = params.get('onetCode')!;
      const onetCodeChanged = this.onetCode !== newOnetCode;
      this.onetCode = newOnetCode;

      // Update careerData if we have matches in store
      if (this.allCareerMatches.length > 0) {
        this.careerData = this.allCareerMatches.find(e => e.onet_code == this.onetCode);
      }

      // Load details from API whenever onetCode changes
      if (onetCodeChanged || !this.careerDetails) {
        localStorage.setItem(ONET_LOCAL_KEY, this.onetCode);
        this.loadCareerDetails(this.onetCode);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCareerDetails(onetCode: string) {
    this.loading = true;
    this.error = null;
    this.announcer.announce('Loading career details');

    this.careerDetailsService.getCareerDetails(onetCode)
      .pipe(
        catchError(error => {
          console.error('Error loading career details:', error);
          this.error = 'Failed to load career details. Please try again.';
          this.loading = false;
          this.announcer.announceAfterLoading('Career details failed to load.');
          return of(null);
        })
      )
      .subscribe(response => {
        if (response?.result) {
          this.careerDetails = response.result;
        }
        this.loading = false;
        this.announcer.announceAfterLoading('Career details loaded.');
      });
  }

  redirectToExploreCareers() {
    this.router.navigate(['/career-matches']);
  }

  viewProfile() {
    analytics.track('Career Details Survey Results', {
      section: 'explore'
    });
    this.store.select('riasecScore').subscribe(riasecScore => {
      if (riasecScore) {
        this.dialog.open(ScoresComponent, {
          data: { riasec: riasecScore, source: 'viewProfile' },
          disableClose: true,
          maxHeight: '90vh',
        });
      } else {
        // try retrieve from localStorage
        let storedRiasecScore = null;
        try {
          const stored = localStorage.getItem('riasec_score');
          if (stored) {
            storedRiasecScore = JSON.parse(stored);
          }
        } catch (error) {
          console.warn('Failed to load RIASEC score from localStorage:', error);
        }

        if (storedRiasecScore) {
          this.dialog.open(ScoresComponent, {
            data: { riasec: storedRiasecScore, source: 'viewProfile' },
            disableClose: true,
            maxHeight: '90vh',
          });
        } else {
          // Final fallback to mock data if no score is stored
          console.warn('No RIASEC score found in store or localStorage, using fallback data');
          const fallbackRiasecData = {
            realistic: 20,
            investigative: 18,
            artistic: 12,
            social: 25,
            enterprising: 20,
            conventional: 15
          };

          this.dialog.open(ScoresComponent, {
            data: { riasec: fallbackRiasecData, source: 'viewProfile' },
            disableClose: true,
            maxHeight: '90vh',
          });
        }
      }
    }).unsubscribe();
  }

  // onScroll() {
  //   const el = this.contentDiv.nativeElement;
  //   el.classList.add('show-scrollbar');

  //   clearTimeout(this.scrollTimeout);
  //   this.scrollTimeout = setTimeout(() => {
  //     el.classList.remove('show-scrollbar');
  //   }, 1000);
  // }

  // Helper methods to get data from either API response or store data
  get title(): string {
    return this.careerDetails?.title || this.careerData?.title || 'Career Title';
  }

  get alternativeNames(): string {
    if (this.careerDetails?.alternativeNames?.length) {
      return this.careerDetails.alternativeNames.join(', ');
    }
    return 'Alternative names not available';
  }

  get jobSummary(): string {
    return this.careerDetails?.jobSummary || 'Job summary not available';
  }

  get typicalTasks(): string[] {
    return this.careerDetails?.typicalTasks || [];
  }

  get topSkills(): string[] {
    return this.careerDetails?.topSkills || [];
  }

  get interestTypes(): string {
    if (this.careerDetails?.interestType?.length) {
      // Convert array like ["Conventional", "Artistic"] to "C/A"
      const abbreviations = this.careerDetails.interestType.map(type => type.charAt(0)).join('/');
      const fullNames = this.careerDetails.interestType.join(' and ');
      return `${abbreviations} - ${fullNames}`;
    }
    return 'Interest types not available';
  }

  get areaMedianSalary(): { area: string | null; areaType: string | null; medianSalary: string | null } {
    return this.careerDetails?.areaMedianSalary || {
      area: null,
      areaType: null,
      medianSalary: null
    };
  }

  get areaGrowthProjection(): { area: string | null; areaType: string | null; growthCategory: string | null; percentChange: number | null } {
    return this.careerDetails?.areaGrowthProjection || {
      area: null,
      areaType: null,
      growthCategory: null,
      percentChange: null
    };
  }

  get medianSalary(): string | null {
    return this.careerDetails?.medianSalary || null;
  }

  get jobGrowth(): { growth_category: string; percent_change: number } {
    return this.careerDetails?.jobGrowth || { growth_category: 'N/A', percent_change: 0 };
  }

  get educationZone(): number {
    //console.log('Education Zone:', this.careerDetails?.education?.zone);
    return this.careerDetails?.education?.zone || 3; // Default to zone 3
  }

  get workSetting() {
    //console.log('Work Setting:', this.careerDetails?.workSetting);
    return this.careerDetails?.workSetting || {
      hoursWeek: { check: undefined },
      timePressure: 0,
      contactWithOthers: 0,
      publicSpeaking: 0,
      freedomToMakeDecisions: 0,
      teamWork: 0
    };
  }

  get relatedCareers() {
    const baseRelatedCareers = this.careerDetails?.relatedCareers || [];

    return baseRelatedCareers.map(career => {
      const matchingCareer = this.allCareerMatches.find(match =>
        match.title === career.title
      );


      const careerIcon = matchingCareer?.icon ||
        getCareerIcon((career as any).onet_career_cluster?.[0] || 'default');

      return {
        ...career,
        match_score: matchingCareer?.match_score || 'low',
        icon: careerIcon,
      };
    });
  }

  // Helper methods for work setting display
  getScaleLabel(value: number | string): string {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    const filledBars = Math.round(numericValue);

    if (filledBars >= 4) return 'High';
    if (filledBars >= 3) return 'Medium';
    return 'Low';
  }

  getScaleBars(value: number | string): boolean[] {
    const bars = [false, false, false, false, false];
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    const filledBars = Math.round(numericValue);

    const clampedBars = Math.max(0, Math.min(filledBars, 5));

    for (let i = 0; i < clampedBars; i++) {
      bars[i] = true;
    }
    return bars;
  }

  getEducationIcons(): boolean[] {
    const icons = [false, false, false, false, false];
    const zone = this.educationZone;
    for (let i = 0; i < Math.min(zone, 5); i++) {
      icons[i] = true;
    }
    return icons;
  }

  getEducationDescription(): string {
    const zone = this.educationZone;
    switch (zone) {
      case 1: return 'Some of these occupations may require a high school diploma or GED certificate.';
      case 2: return 'These occupations usually require a high school diploma.';
      case 3: return 'Most occupations in this zone require training in vocational schools, related on-the-job experience, or an associate\'s degree.';
      case 4: return 'Most of these occupations require a four-year bachelor\'s degree, but some do not.';
      case 5: return 'Most of these occupations require graduate school. For example, they may require a master\'s degree, and some require a Ph.D., M.D., or J.D. (law degree).';
      default: return 'Preparation level not specified.';
    }
  }

  onOnetLinkClick() {
    analytics.track('Onet Link Navigate');
  }

  onPmpLinkClick() {
    analytics.track('PMP Link Navigate');
  }
}
